import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@shared/api/api-context';
import { isApiError } from '@shared/api/errors/errors';
import {
  mapInvoiceDtoToPublicPay,
  resolveInvoicePayUiState,
  shouldPollInvoice,
  getPollingIntervalMs,
  type PublicInvoicePay,
  type InvoicePayUiState,
} from '@entities/invoice-pay';

const LOAD_TIMEOUT_MS = 10_000;
const CHECK_COOLDOWN_MS = 15_000;
const POST_EXPIRY_CHECK_MS = 10 * 60 * 1000;

/**
 * Public invoice pay flow.
 *
 * Realtime confirmations: HTTP polling only (no WS/SSE).
 * - Backend `TransactionConfirmationsJob` (~30s) updates tx confirmations.
 * - Client polls `GET /api/v1/invoices/:id` while PAYMENT / PAYMENT_DETECTED / CONFIRMING.
 * - `forceCheck` triggers the same GET (legacy «Проверить»).
 */
export interface UseInvoicePayResult {
  invoice: PublicInvoicePay | null;
  uiState: InvoicePayUiState;
  isLoading: boolean;
  loadError: boolean;
  isProceeding: boolean;
  isChecking: boolean;
  checkCooldownLeft: number;
  selectedCurrency: string;
  selectedNetwork: string;
  setSelectedCurrency: (code: string) => void;
  setSelectedNetwork: (code: string) => void;
  reload: () => Promise<void>;
  proceedToPayment: () => Promise<boolean>;
  forceCheck: () => Promise<'found' | 'not_found' | 'error' | 'rate_limited'>;
  onTimerExpire: () => Promise<void>;
  pollErrorStreak: number;
  announceKey: 'paymentDetected' | 'paymentConfirmed' | null;
}

export function useInvoicePay(invoiceId: string | undefined): UseInvoicePayResult {
  const api = useApi();
  const [invoice, setInvoice] = useState<PublicInvoicePay | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isProceeding, setIsProceeding] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkCooldownLeft, setCheckCooldownLeft] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [checkingAfterExpiry, setCheckingAfterExpiry] = useState(false);
  const [pollErrorStreak, setPollErrorStreak] = useState(0);
  const [announceKey, setAnnounceKey] = useState<'paymentDetected' | 'paymentConfirmed' | null>(null);

  const lastCheckAt = useRef(0);
  const expiryCheckStartedAt = useRef<number | null>(null);
  const prevHadTx = useRef(false);
  const initializedSelection = useRef(false);

  const applyInvoice = useCallback((next: PublicInvoicePay) => {
    setInvoice(next);

    if (!initializedSelection.current) {
      setSelectedCurrency(next.currency);
      setSelectedNetwork(next.network);
      initializedSelection.current = true;
    }

    const hasTx = Boolean(next.txid) || next.confirmations.current > 0;
    if (hasTx && !prevHadTx.current) {
      setAnnounceKey('paymentDetected');
    }
    if ((next.status === 'paid' || next.status === 'overpaid') && prevHadTx.current) {
      setAnnounceKey('paymentConfirmed');
    }
    prevHadTx.current = hasTx;

    if (next.status === 'expired' || next.status === 'paid' || next.status === 'overpaid' || next.status === 'error') {
      setCheckingAfterExpiry(false);
    }
  }, []);

  const fetchInvoice = useCallback(async (): Promise<PublicInvoicePay | null> => {
    if (!invoiceId) return null;
    const dto = await api.invoices.getInvoice(invoiceId);
    const mapped = mapInvoiceDtoToPublicPay(dto);
    applyInvoice(mapped);
    setLoadError(false);
    setPollErrorStreak(0);
    return mapped;
  }, [api.invoices, applyInvoice, invoiceId]);

  const reload = useCallback(async () => {
    if (!invoiceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(false);
    initializedSelection.current = false;

    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      setLoadError(true);
      setIsLoading(false);
    }, LOAD_TIMEOUT_MS);

    try {
      await fetchInvoice();
      if (!timedOut) setIsLoading(false);
    } catch {
      if (!timedOut) {
        setLoadError(true);
        setInvoice(null);
        setIsLoading(false);
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }, [fetchInvoice, invoiceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const uiState: InvoicePayUiState =
    loadError && !invoice
      ? 'ERROR'
      : isLoading && !invoice
        ? 'LOADING'
        : resolveInvoicePayUiState(invoice, { checkingAfterExpiry });

  useEffect(() => {
    if (!invoiceId || !shouldPollInvoice(uiState)) return undefined;

    let cancelled = false;
    let timer: number | undefined;

    const run = async () => {
      if (cancelled || document.hidden) return;
      try {
        await fetchInvoice();
      } catch {
        setPollErrorStreak((n) => n + 1);
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        await run();
        if (!cancelled) schedule();
      }, getPollingIntervalMs(uiState));
    };

    schedule();

    const onVisibility = () => {
      if (!document.hidden) void run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchInvoice, invoiceId, uiState]);

  useEffect(() => {
    if (checkCooldownLeft <= 0) return undefined;
    const id = window.setInterval(() => {
      setCheckCooldownLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [checkCooldownLeft]);

  const onTimerExpire = useCallback(async () => {
    try {
      const next = await fetchInvoice();
      if (!next) return;
      if (next.status === 'expired') {
        setCheckingAfterExpiry(false);
        return;
      }
      if (next.status === 'pending' || next.status === 'underpaid') {
        setCheckingAfterExpiry(true);
        expiryCheckStartedAt.current = Date.now();
      }
    } catch {
      // keep UI; polling continues
    }
  }, [fetchInvoice]);

  useEffect(() => {
    if (!checkingAfterExpiry) return undefined;
    const id = window.setInterval(() => {
      const started = expiryCheckStartedAt.current;
      if (!started || Date.now() - started <= POST_EXPIRY_CHECK_MS) return;
      void fetchInvoice().then((next) => {
        if (!next || next.status === 'expired') setCheckingAfterExpiry(false);
      });
    }, 5000);
    return () => window.clearInterval(id);
  }, [checkingAfterExpiry, fetchInvoice]);

  const proceedToPayment = useCallback(async (): Promise<boolean> => {
    if (!invoiceId) return false;
    setIsProceeding(true);
    try {
      const refreshed = await fetchInvoice();
      return Boolean(refreshed?.address);
    } catch {
      return false;
    } finally {
      setIsProceeding(false);
    }
  }, [fetchInvoice, invoiceId]);

  const forceCheck = useCallback(async (): Promise<'found' | 'not_found' | 'error' | 'rate_limited'> => {
    if (!invoiceId) return 'error';
    const now = Date.now();
    if (lastCheckAt.current > 0 && now - lastCheckAt.current < CHECK_COOLDOWN_MS) {
      return 'rate_limited';
    }

    setIsChecking(true);
    lastCheckAt.current = now;
    setCheckCooldownLeft(Math.ceil(CHECK_COOLDOWN_MS / 1000));

    try {
      const dto = await api.invoices.checkInvoice(invoiceId);
      const mapped = mapInvoiceDtoToPublicPay(dto);
      applyInvoice(mapped);
      const hasTx = Boolean(mapped.txid) || mapped.confirmations.current > 0;
      const paid = mapped.status === 'paid' || mapped.status === 'overpaid';
      return hasTx || paid ? 'found' : 'not_found';
    } catch (error) {
      if (isApiError(error) && error.status === 429) return 'rate_limited';
      return 'error';
    } finally {
      setIsChecking(false);
    }
  }, [api.invoices, applyInvoice, invoiceId]);

  return {
    invoice,
    uiState,
    isLoading,
    loadError,
    isProceeding,
    isChecking,
    checkCooldownLeft,
    selectedCurrency,
    selectedNetwork,
    setSelectedCurrency,
    setSelectedNetwork,
    reload,
    proceedToPayment,
    forceCheck,
    onTimerExpire,
    pollErrorStreak,
    announceKey,
  };
}
